import { curry, path, pick } from 'ramda';
import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { AsyncState } from 'react-use/esm/useAsyncFn';
import { MediaManagerSDKBoardSitesVisibility } from '../../../../../constants/BoardSitesVisibility';
import { MediaType } from '../../../../../constants/MediaType';
import { getReadableFileSize } from '../../../../../utils/files';
import {
  MediaManagerSDKAnyItem,
  MediaManagerSDKBoard,
  MediaManagerSDKFile,
  MediaManagerSDKUser,
} from '../../../../../utils/media-manager-sdk';
import { isBoard } from '../../../../../utils/media-manager-sdk/boards';
import {
  canCopyLinkBeProvided,
  convertBitrate,
  getFileExtension,
  hasResolution,
  hasSize,
  hasUser,
  isFolder,
  isItemFromVimeoCreator,
  isPurchasedFromShutterstock,
  isSiteIconFile,
} from '../../../../../utils/media-manager-sdk/items';
import { memoize, memoizedOver } from '../../../../../utils/memoize';
import { convertMSSToMMSS } from '../../../../../utils/time';

import { Loader, Text, TextButton } from '../../../../../components/common';
import { ITEM_INFO_BOTTOM_PART_TRANSLATIONS } from './ItemInfoTable.constants';
import { ITransformInvoker } from './ItemInfoTable.types';

export type TPropsTransformer = (item: MediaManagerSDKAnyItem, props: ITransformInvoker) => React.ReactNode;

export type TPropsTransformers = { [key: string]: TPropsTransformer };

const renderAsyncInfoEntry = (asyncInfoEntry: AsyncState<MediaManagerSDKUser>, { pending, rejected, fulfilled }) => {
  if (asyncInfoEntry.loading) {
    return pending(asyncInfoEntry);
  }

  if (asyncInfoEntry.error) {
    return rejected(asyncInfoEntry);
  }

  return fulfilled(asyncInfoEntry);
};

const WrapWithText: React.FC = ({ children }) => (
  <Text weight="thin" size="tiny" secondary={false} light={false}>
    {children}
  </Text>
);

const PROPS_TRANSFORMERS: TPropsTransformers = {
  type: (file: MediaManagerSDKFile) =>
    isSiteIconFile(file) ? (
      <WrapWithText>
        <FormattedMessage id="panel.bottom-parts.single-item-bottom-part.icon-file-type"></FormattedMessage>
      </WrapWithText>
    ) : (
      <WrapWithText>{getFileExtension(file)}</WrapWithText>
    ),
  size: ({ fileSize }: MediaManagerSDKFile, { formatNumber }) => (
    <WrapWithText>{getReadableFileSize(fileSize, formatNumber)}</WrapWithText>
  ),
  resolution: ({ width, height }: MediaManagerSDKFile) => (
    <WrapWithText>
      <FormattedMessage id="common.resolution" values={{ width, height }} />
    </WrapWithText>
  ),
  createdAt: ({ createdAt }: MediaManagerSDKAnyItem) =>
    createdAt && (
      <WrapWithText>
        <FormattedDate value={new Date(createdAt * 1000)} day="numeric" year="numeric" month="short" />
      </WrapWithText>
    ),
  createdBy: (_item, { asyncInfo }) =>
    renderAsyncInfoEntry(asyncInfo, {
      pending: () => <Loader size="small" />,
      rejected: () => (
        <WrapWithText>
          <FormattedMessage id="panel.bottom-parts.single-item-bottom-part.created-by-visitor" />
        </WrapWithText>
      ),
      fulfilled: (userInfo) => <WrapWithText>{path(['value', 'email'], userInfo)}</WrapWithText>,
    }),
  duration: ({ fileInput }: MediaManagerSDKFile) =>
    fileInput && <WrapWithText>{convertMSSToMMSS(fileInput.duration)}</WrapWithText>,
  bitrate: ({ fileInput }: MediaManagerSDKFile) =>
    fileInput && <WrapWithText>{convertBitrate(fileInput.bitrate)}</WrapWithText>,
  url: (_item: MediaManagerSDKFile, { copyableItemUrl, handleCopyUrlClick, addUrlCopiedNotification }) =>
    copyableItemUrl && (
      <TextButton
        weight="normal"
        size="tiny"
        dataHook="copy-url-panel-button"
        onClick={memoizedOver(handleCopyUrlClick, addUrlCopiedNotification)}
      >
        <FormattedMessage id={ITEM_INFO_BOTTOM_PART_TRANSLATIONS.copyUrl} />
      </TextButton>
    ),
  copyWixUrl: (_item: MediaManagerSDKFile, { handleCopyWixUrlClick, addUrlCopiedNotification }) => (
    <TextButton
      weight="normal"
      size="tiny"
      dataHook="copy-wix-url-panel-button"
      onClick={memoizedOver(handleCopyWixUrlClick, addUrlCopiedNotification)}
    >
      <FormattedMessage id={ITEM_INFO_BOTTOM_PART_TRANSLATIONS.copyWixUrl} />
    </TextButton>
  ),
  sitesVisibility: (item: MediaManagerSDKBoard) => (
    <WrapWithText>
      {item.sitesVisibility === MediaManagerSDKBoardSitesVisibility.All ? (
        <FormattedMessage id="panel.bottom-parts.single-item-bottom-part.sites-visibility.all" />
      ) : (
        <FormattedMessage id="panel.bottom-parts.single-item-bottom-part.sites-visibility.current" />
      )}
    </WrapWithText>
  ),
  tagsPaid: () => (
    <WrapWithText>
      <FormattedMessage id="panel.bottom-parts.single-item-bottom-part.shutterstock" />
    </WrapWithText>
  ),
  madeWith: () => (
    <WrapWithText>
      <FormattedMessage id="panel.bottom-parts.single-item-bottom-part.vimeo-creator" />
    </WrapWithText>
  ),
};

type TGetPropsTransformersKeys = (item: MediaManagerSDKAnyItem) => string[];
const getPropsTransformersKeys: TGetPropsTransformersKeys = (item) => {
  const conditionalProps = hasUser(item) ? ['createdBy'] : [];

  if (isPurchasedFromShutterstock(item)) {
    conditionalProps.push('tagsPaid');
  }

  if (isBoard(item)) {
    return ['createdAt', ...conditionalProps, 'sitesVisibility'];
  }

  if (isFolder(item)) {
    return ['createdAt', ...conditionalProps];
  }

  switch (item.mediaType) {
    case MediaType.Video:
    case MediaType.SecureVideo:
      return [
        'type',
        ...(hasSize(item) ? ['size'] : []),
        ...(hasResolution(item) ? ['resolution'] : ['']),
        ...(canCopyLinkBeProvided(item) ? ['url', 'copyWixUrl'] : ['']),
        'duration',
        'createdAt',
        ...(isItemFromVimeoCreator(item) ? ['madeWith'] : ['']),
        ...conditionalProps,
      ];

    case MediaType.Picture:
    case MediaType.ThreeD:
    case MediaType.SecurePicture:
    case MediaType.VectorArt:
    case MediaType.SiteIcon:
      return [
        'type',
        ...(hasSize(item) ? ['size'] : []),
        ...(hasResolution(item) ? ['resolution'] : ['']),
        ...(canCopyLinkBeProvided(item) ? ['url', 'copyWixUrl'] : ['']),
        'createdAt',
        ...conditionalProps,
      ];

    case MediaType.SWF:
    case MediaType.Document:
    case MediaType.SecureDocument:
    case MediaType.Zip:
    case MediaType.SecureZip:
      return [
        'type',
        ...(hasSize(item) ? ['size'] : []),
        ...(canCopyLinkBeProvided(item) ? ['url'] : ['']),
        'createdAt',
        ...conditionalProps,
      ];

    case MediaType.Music:
    case MediaType.SecureMusic:
      return [
        'type',
        ...(hasSize(item) ? ['size'] : []),
        ...(canCopyLinkBeProvided(item) ? ['url', 'copyWixUrl'] : ['']),
        'duration',
        'bitrate',
        'createdAt',
        ...conditionalProps,
      ];

    default:
      return [];
  }
};

export const getPropsTransformers: (item: MediaManagerSDKAnyItem) => Partial<TPropsTransformers> = memoize((item) =>
  pick(getPropsTransformersKeys(item), PROPS_TRANSFORMERS),
);

export const invokeTransformerWithItem = curry<MediaManagerSDKAnyItem, ITransformInvoker, TPropsTransformer, any>(
  (item, props, transformer) => transformer(item, props),
);
